import { useRef, useState } from 'react';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Card, Form, Row } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';

import FormField from '@/components/FormField';
import LoadingSpinner from '@/components/LoadingSpinner';
import biFormFields from '@/config/programs/biFormFields';
import biFormTitles from '@/config/programs/biFormTitles';
import { useQueryBIRequestResult } from '@/hooks/useQueryBIRequestResult';
import useGlobalConfig from '@/store/global';

import BiRequestResult from './result.tsx';

const BiRequest = () => {
  const { program, themeColor } = useGlobalConfig();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { biRequestId } = useParams();
  const cardRef = useRef(null);
  const [form] = Form.useForm();
  const [resultId, setResultId] = useState<number | undefined>();

  const formFields = biFormFields[program?.name as keyof typeof biFormFields];
  const { data: biRequestResult, isLoading: biRequestResultLoading } = useQueryBIRequestResult(
    resultId as number
  );
  const showBIResult =
    biRequestResult?.status === 'BI Completed' || biRequestResult?.status === 'Patient Informed';

  // Safe date parsing function
  const parseDate = (dateStr: string) => {
    if (!dateStr) return null;
    const parsed = dayjs(dateStr);
    return parsed.isValid() ? parsed : null;
  };

  const loadAssistanceRequestData = async () => {
    try {
      const { data } = await axios.get(`benefit_investigation_requests/${biRequestId}`);
      const {
        id,
        created_at,
        updated_at,
        verified_at,
        patient_dob,
        insurance_card,
        signed_pdf,
        patient_signature_date,
        licensed_prescriber_signature_date,
        secondary_policy_holder_dob,
        primary_policy_holder_dob,
        ...rest
      } = data;

      setResultId(data.benefit_investigation_result_id);

      // Format file lists for Upload components
      const formatFileList = (file: any) => {
        if (!file?.url) return [];
        return [
          {
            uid: '-1',
            name: 'View the uploaded file',
            status: 'done',
            url: file.url,
          },
        ];
      };

      form.setFieldsValue({
        ...rest,
        created_at: parseDate(created_at),
        updated_at: parseDate(updated_at),
        verified_at: parseDate(verified_at),
        patient_dob: parseDate(patient_dob),
        insurance_card: formatFileList(insurance_card),
        signed_pdf_file: formatFileList(signed_pdf),
        patient_signature_date: parseDate(patient_signature_date),
        licensed_prescriber_signature_date: parseDate(licensed_prescriber_signature_date),
        primary_policy_holder_dob: parseDate(primary_policy_holder_dob),
        secondary_policy_holder_dob: parseDate(secondary_policy_holder_dob),
      });
    } catch (error: any) {
      if (error.status === 404) {
        navigate('/not-found');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssistanceRequestData();
  }, []);

  if (loading || biRequestResultLoading) {
    return <LoadingSpinner />;
  }

  if (showBIResult) {
    return <BiRequestResult data={biRequestResult} />;
  }

  return (
    <>
      <Card className="my-10 bg-white print:my-0 print:border-none print:p-0">
        <Form form={form} layout="vertical" className="mt-10 bg-white">
          <div className="" ref={cardRef}>
            <h3
              className="mb-10 border-b pb-6 text-center text-xl md:text-3xl font-bold text-[#284095]"
              style={{ color: themeColor }}
            >
              {biFormTitles[program?.name as keyof typeof biFormTitles] || ''}
            </h3>

            {formFields.map((section) => (
              <div key={section.title}>
                <div
                  className="bg-[#20419b] px-2 text-xl font-semibold uppercase text-white"
                  style={{ backgroundColor: themeColor }}
                >
                  {section.title}
                </div>
                <Row gutter={[16, 0]} className="mt-4">
                  {section.fields.map((field) => (
                    <FormField disabled key={field.name} field={field} />
                  ))}
                </Row>
              </div>
            ))}
          </div>
        </Form>
      </Card>
    </>
  );
};

export default BiRequest;
